import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { BLOG_POSTS_PER_PAGE } from '@root/constants/blog';
import BlogIndex from 'components/Blog';
import BlogMore from 'components/Blog/More';
import { getBlogIndex } from 'services/blog';
import { withPageStaticProps } from 'utils/header';
import { formatMonth, getParameterByName } from 'utils/utils';
import type { BlogPostFragment } from 'types';

type BlogPageProps = {
  error: boolean;
  featured: BlogPostFragment[];
  front: BlogPostFragment[];
  posts: BlogPostFragment[];
  primary: BlogPostFragment;
  title: string;
  totalPosts: number;
};

export type BlogDownstreamProps = {
  page: number;
  totalPages: number;
  updatePage: (page: number) => void;
} & BlogPageProps;

export const excerpt = ({ excerpt, bodyRaw: body }: BlogPostFragment) => {
  let trimmed;

  if (excerpt) {
    trimmed = excerpt.substr(0, 120);
    trimmed.substr(0, Math.min(trimmed.length, trimmed.lastIndexOf(' ')));
  } else {
    trimmed = body
      .map((block: any) => {
        if (block._type !== 'block' || !block.children) {
          return '';
        }

        return block.children.map((child: any) => child.text).join('');
      })
      .join(' ')
      .substr(0, 120);
    trimmed = trimmed.substr(0, Math.min(trimmed.length, trimmed.lastIndexOf(' ')));
  }

  if (!trimmed) {
    return '';
  }

  return trimmed + (trimmed.endsWith('.') || trimmed.endsWith('?') || trimmed.endsWith('!') ? '' : '...');
};

export const getTotalPages = (totalPosts: number) => {
  return Math.ceil((totalPosts - 5) / BLOG_POSTS_PER_PAGE) + 1;
};

export const formatPublishedAt = (dateString: string) => {
  if (!dateString) return '';

  // get rid of TZ
  const publishedAtDate = new Date(dateString.replace('Z', ''));

  return `${formatMonth(
    publishedAtDate.getMonth() + 1,
    publishedAtDate.getFullYear(),
  )}, ${publishedAtDate.getDate()} ${publishedAtDate.getFullYear()}`;
};

const BlogPage = (props: BlogPageProps) => {
  const [page, setPage] = useState(1);
  const router = useRouter();

  useEffect(() => {
    const qsPage = getParameterByName('page') ? Number(getParameterByName('page')) : page;

    if (qsPage && qsPage !== page) {
      setPage(qsPage);
    }
  }, []);

  const handleUpdatePage = (page: number) => {
    router.push({
      query: {
        page: String(page),
      },
    });

    setPage(page);
  };

  const { totalPosts } = props;
  const totalPages = getTotalPages(totalPosts);

  if (page > 1) {
    return <BlogMore {...props} page={page} totalPages={totalPages} updatePage={handleUpdatePage} />;
  }

  return <BlogIndex {...props} page={page} totalPages={totalPages} updatePage={handleUpdatePage} />;
};

export const getStaticProps = withPageStaticProps(async () => {
  try {
    const response = await getBlogIndex();
    const totalPosts = response.allBlogPost.length;
    const primary = response.allBlogPost[0];
    const front = response.allBlogPost.slice(1, 5);

    return {
      props: {
        error: false,
        featured: response.allBlogSettings[0].featuredPosts,
        front,
        posts: response.allBlogPost,
        primary,
        title: response.allBlogSettings[0].title,
        totalPosts,
      },
    };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get blog posts');
  }
});

export default BlogPage;
